import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  Injectable,
  Injector,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { ButtonTypes } from '@shared/components/input/button/button-types';

export interface ConfirmDialogOptions {
  /** Translation key for the dialog title. */
  title: string;
  /** Translation key for the dialog body message. */
  message: string;
  /** Translation key for the confirm (destructive) action label. Default 'common.confirm'. */
  confirmLabel?: string;
  /** Translation key for the cancel (safe) action label. Default 'common.cancel'. */
  cancelLabel?: string;
  /** Visual treatment for the confirm button. Default 'danger'. */
  confirmVariant?: ButtonTypes;
}

/**
 * Shows confirmation dialogs that can stack on top of existing modals.
 *
 * Unlike ModalService, which manages a single primary modal at a time, this
 * service mounts its dialog directly into the body with a high z-index so it
 * works as a confirmation layer even from inside a modal. It is intentionally
 * narrow in scope — only for yes/no questions like "Are you sure you want to
 * leave?".
 *
 * ```typescript
 * this.confirmDialog.confirm({
 *   title: 'unsavedChanges.title',
 *   message: 'unsavedChanges.message',
 *   confirmLabel: 'unsavedChanges.leave',
 *   cancelLabel: 'unsavedChanges.stay',
 * }).subscribe(confirmed => {
 *   if (confirmed) this.doDestructiveAction();
 * });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class ConfirmDialogService {
  /** Currently mounted dialog, if any. */
  private current?: ComponentRef<ConfirmDialogComponent>;

  /** Container element the dialog is mounted into. */
  private container?: HTMLElement;

  constructor(
    private readonly injector: Injector,
    private readonly appRef: ApplicationRef,
    private readonly envInjector: EnvironmentInjector,
  ) {}

  /**
   * Shows a confirmation dialog. Emits `true` when the user confirms and
   * `false` when they cancel (or press Escape). Always emits exactly once.
   */
  public confirm(options: ConfirmDialogOptions): Observable<boolean> {
    // If a dialog is already showing, dismiss it first so we never stack two
    this.dismiss();

    const result = new Subject<boolean>();

    const container = document.createElement('div');
    container.classList.add('confirm-dialog-container');
    document.body.appendChild(container);
    this.container = container;

    const ref = createComponent(ConfirmDialogComponent, {
      environmentInjector: this.envInjector,
      elementInjector: this.injector,
      hostElement: container,
    });

    ref.instance.title = options.title;
    ref.instance.message = options.message;
    ref.instance.confirmLabel = options.confirmLabel ?? 'common.confirm';
    ref.instance.cancelLabel = options.cancelLabel ?? 'common.cancel';
    ref.instance.confirmVariant = options.confirmVariant ?? 'danger';

    ref.instance.result.pipe(take(1)).subscribe(confirmed => {
      result.next(confirmed);
      result.complete();
      this.dismiss();
    });

    this.appRef.attachView(ref.hostView);
    this.current = ref;

    return result.asObservable();
  }

  /** Tears down the current dialog if one is mounted. */
  private dismiss(): void {
    this.current?.destroy();
    this.current = undefined;
    this.container?.remove();
    this.container = undefined;
  }
}
